import React, { useEffect } from "react";
import { useFeature } from "@nextiva/feature-toggle";
import {
  Icon,
  IconButton,
  Popover,
  Skeleton,
  Stack,
  Typography,
} from "@nextiva/next-ui-components";
import {
  AdminGroupBreadCrumb,
  AdminGroupsHierarchyTree,
  getStoredSelection,
  writeStoredSelection,
} from "@nextiva/next-ui-wayfinder";
import type {
  AdminGroupHierarchyNode,
  AdminGroupNodePayload,
  StoredSelection,
} from "@nextiva/next-ui-wayfinder";
import { useGetAdminGroupHierarchy } from "../../../hooks/useAdminGroup/useGetAdminGroupHierarchy";
import { useLocationNameById } from "../../../hooks/useAdminGroup/useLocationNameById";
import AppData from "app_data";

const slugify = (value = ""): string =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const toSelectedGroup = (
  node: AdminGroupHierarchyNode,
  locationNameById?: Map<string, string>,
): Omit<StoredSelection, "corpAccountFingerprint"> => {
  const isLocation = node?.type === "LOCATION";
  const id = isLocation ? (node?.externalId ?? node?.id) : node?.id;
  const resolvedName = isLocation
    ? (locationNameById?.get(String(node?.externalId ?? node?.name ?? "")) ??
      node?.name)
    : node?.name;
  return {
    id,
    name: resolvedName,
    slug: slugify(resolvedName),
    label: node?.label ?? undefined,
    type: node?.type ?? undefined,
  };
};

// Matches the rendered breadcrumb row's shape so the header doesn't shift
// when the real content lands.
const breadcrumbSkeleton = (
  <Stack
    direction="row"
    alignItems="center"
    gap={1}
    data-testid="Analytics-AdminGroupBreadCrumb-Skeleton"
  >
    <Skeleton variant="text" width={220} sx={{ fontSize: "0.875rem" }} />
    <Skeleton variant="circular" width={16} height={16} />
  </Stack>
);

const AdminGroupBreadCrumbWrapper = () => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const currentCorp: string | number | undefined = AppData.user?.enterprise_id;
  const [selectedGroup, setSelectedGroup] =
    React.useState<StoredSelection | null>(() =>
      getStoredSelection(currentCorp),
    );
  const { isEnabled: isLbaEnabled, isReady: isLbaReady } = useFeature({
    name: "Analytics.LocationBasedAdministration",
  });
  const {
    data: adminGroupHierarchy,
    isLoading,
    isError,
    isFailed,
  } = useGetAdminGroupHierarchy({
    corpAccountNbr: String(currentCorp ?? ""),
    isLbaEnabled: isLbaEnabled && isLbaReady,
  });

  const locationNameById = useLocationNameById({
    corpAccountNbr: String(currentCorp ?? ""),
    isLbaEnabled: isLbaEnabled && isLbaReady,
  });

  // The tree's built-in location-name resolution is dead here (it reads
  // global-store slices this app never populates); this override mirrors its
  // lookup (externalId, then raw name) and also drives the tree search.
  const resolveNodeLabel = React.useCallback(
    (node: AdminGroupHierarchyNode): string => {
      if (node?.type === "LOCATION") {
        const lookupKey = node?.externalId ?? node?.name;
        const resolved = lookupKey
          ? locationNameById.get(String(lookupKey))
          : undefined;
        if (resolved) return resolved;
      }
      return node?.name ?? "";
    },
    [locationNameById],
  );

  const [treeRootNodes, setTreeRootNodes] = React.useState<
    AdminGroupHierarchyNode[]
  >([]);

  const handleDataLoaded = React.useCallback(
    (rootNodes: AdminGroupHierarchyNode[]) => {
      setTreeRootNodes(rootNodes);
    },
    [],
  );

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNodeClick = ({
    node,
  }: AdminGroupNodePayload<AdminGroupHierarchyNode>) => {
    const selected = toSelectedGroup(node, locationNameById);

    writeStoredSelection(selected, currentCorp);
    setSelectedGroup(selected);
    setAnchorEl(null);
  };

  useEffect(() => {
    setSelectedGroup(getStoredSelection(currentCorp));
  }, [currentCorp]);

  useEffect(() => {
    if (isFailed) setAnchorEl(null);
  }, [isFailed]);

  useEffect(() => {
    if (!treeRootNodes.length || !treeRootNodes[0]?.id) return;

    const stored = getStoredSelection(currentCorp);
    if (stored?.id) {
      setSelectedGroup(stored);
      return;
    }

    const defaultSelected = toSelectedGroup(treeRootNodes[0], locationNameById);
    writeStoredSelection(defaultSelected, currentCorp);
    setSelectedGroup(defaultSelected);
  }, [treeRootNodes, currentCorp, locationNameById]);

  const crumbs = React.useMemo(() => {
    const base = ["Admin portal", "Admin group"];
    // A failed hierarchy load applies no scope (useScopedWayfinderSelection),
    // so showing the stored name would claim a filter that isn't in effect.
    // isFailed, unlike isError, holds through in-flight retries -- no name
    // flash on navigation while the endpoint is down.
    if (isFailed) return base;
    // Stored LOCATION selections may hold the raw numeric name; re-resolve at
    // render. Their stored id is the externalId, i.e. the map's locationId key.
    const resolvedName =
      selectedGroup?.type === "LOCATION" && selectedGroup?.id
        ? (locationNameById.get(String(selectedGroup.id)) ?? selectedGroup.name)
        : selectedGroup?.name;
    return resolvedName ? [...base, resolvedName] : base;
  }, [selectedGroup, isFailed, locationNameById]);

  const treeEmptyContent = isLoading ? (
    <Stack
      gap={1}
      sx={{ width: "100%", p: 1 }}
      data-testid="Analytics-AdminGroupTree-Skeleton"
    >
      {[0, 1, 2, 3].map((index) => (
        <Stack
          key={index}
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ pl: index % 2 === 1 ? 3 : 0 }}
        >
          <Skeleton variant="circular" width={16} height={16} />
          <Skeleton
            variant="text"
            width={`${60 - index * 8}%`}
            sx={{ fontSize: "0.875rem" }}
          />
        </Stack>
      ))}
    </Stack>
  ) : isError ? (
    <Typography variant="body2">Unable to load admin groups.</Typography>
  ) : (
    <Typography variant="body2">No admin groups found</Typography>
  );

  if (isLbaReady && !isLbaEnabled) {
    return null;
  }

  // While the flag resolves, only reserve space when a stored selection hints
  // the account uses LBA -- otherwise non-LBA loads would flash a skeleton.
  if (!isLbaReady) {
    return selectedGroup ? breadcrumbSkeleton : null;
  }

  // First hierarchy load. !isFailed keeps retries of a failed query on the
  // base-crumbs path instead of flashing a skeleton.
  if (isLoading && !isFailed) {
    return breadcrumbSkeleton;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0}>
      <AdminGroupBreadCrumb crumbs={crumbs} />
      <IconButton
        onClick={handleClick}
        aria-label="Open admin group selector"
        aria-controls={open ? id : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Icon
          name="menu"
          icon="chevronDown"
          size={16}
          sx={{ color: "#005fec", fontWeight: "bold" }}
        />
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <AdminGroupsHierarchyTree
          data={adminGroupHierarchy ?? []}
          onNodeClick={handleNodeClick}
          onDataLoaded={handleDataLoaded}
          getNodeLabel={resolveNodeLabel}
          selectedId={selectedGroup?.id ?? null}
          showLocations
          showNodeLabels={false}
          dataTestIdPrefix="Analytics-AdminGroupTree"
          indentSize={0}
          search={{ autoFocus: true }}
          emptyContent={treeEmptyContent}
          styles={{
            root: { minWidth: 280, maxHeight: 400, overflowY: "auto" },
          }}
        />
      </Popover>
    </Stack>
  );
};

export default AdminGroupBreadCrumbWrapper;
